'use client';

import { CloseIcon, Drawer, Progress, Select, showToast, styled } from '@attentive-platform/stem-ui';
import { getAPI, numberToUUID, patchAPI } from 'utils';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { EstimatorType, TakeoffAssignmentScopeType, TakeoffStatusEnum } from './types';
import { getLocalStorage } from '../utils';
import { TradeScope } from './TradeScope';
import { completionTag, drawerHeader, drawerHeaderLine, styledFlex, infoText, styledDrawer, loader } from './styles';
import { StyleProvider } from '../StyleProvider';
import { Button } from '../Button';
import { useScopeStore } from './useScopeChange';
import { StatusUpdateConfirmationModal } from './StatusUpdateConfirmationModal';

const FilterFooter = styled('div')(() => ({
  width: '19.8125rem',
  boxSizing: 'border-box',
  boxShadow: '0px -0.25rem 0.75rem var(--color-neutral-20)',
  position: 'fixed',
  bottom: 0,
  padding: '1rem 1.5rem',
  zIndex: 1000,
  background: 'var(--color-background-white)',
  '.box': {
    width: 'fit-content',
    marginLeft: 'auto'
  }
}));

const StyledSelect = styled(Select)(() => ({
  width: '8.6875rem'
}));

type ScopeChangePanelProps = {
  open: boolean;
  setClose: (val: boolean) => void;
  requestId: string;
  baseUrl: string;
  onSuccess?: () => void;
  showMessage?: any;
};

type ErrorType = { response: { data: Record<string, string[]> } };

const getTotals = (items: Record<string, any>[], key: string) =>
  (items || []).reduce((acc, curr) => acc + curr[key], 0);

export const ScopeChangePanel = ({
  open,
  setClose,
  requestId,
  baseUrl,
  onSuccess,
  showMessage = showToast
}: ScopeChangePanelProps) => {
  const user = getLocalStorage('user');
  const parsedUser = JSON.parse(user || '{}');
  const authenticatedUser =
    typeof parsedUser.id === 'number' ? { ...parsedUser, id: numberToUUID(parsedUser.id) } : parsedUser;

  const [fetchingAssignments, setFetchingAssignments] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<string | null>(null);

  const { setInitialAssignments, trades, selectedTrade, setSelectedTrade, getChangedAssignments, resetStore } =
    useScopeStore(
      useShallow(state => ({
        setInitialAssignments: state.setInitialAssignments,
        trades: state.trades,
        selectedTrade: state.selectedTrade,
        setSelectedTrade: state.setSelectedTrade,
        getChangedAssignments: state.getChangedAssignments,
        resetStore: state.resetStore
      }))
    );

  const totalScopeItems = getTotals(trades[selectedTrade]?.assignments, 'total_scope_items');
  const totalCompletedItems = getTotals(trades[selectedTrade]?.assignments, 'completed_scope_items');
  const totalReviewedItems = getTotals(trades[selectedTrade]?.assignments, 'reviewed_scope_items');

  const userAssignment = trades[selectedTrade]?.assignments?.find(
    assignment => assignment.user.id === authenticatedUser.id
  );

  const isQC = userAssignment?.estimator_type === EstimatorType.QUALITY_CONTROL;

  const isQA = userAssignment?.estimator_type === EstimatorType.QUALITY_ASSURANCE;

  const handleSave = async () => {
    const payload = getChangedAssignments();
    if (payload)
      try {
        setBtnLoading(true);
        await patchAPI(`${baseUrl}/takeoff/${requestId}/assignments/`, payload);
        showMessage('Scope updated successfully', 'success');
        const response = await getAPI<TakeoffAssignmentScopeType>(`${baseUrl}/takeoff/${requestId}/assignments/`);
        setInitialAssignments(response, authenticatedUser);
        onSuccess?.();
      } catch (err) {
        const errors = (err as ErrorType)?.response?.data ? Object.values((err as ErrorType).response.data).flat() : [];
        showMessage(errors.join(', '), 'error');
      } finally {
        setBtnLoading(false);
      }
  };

  const verifyStatusUpdate = () => {
    Object.values(trades).forEach(trade => {
      if (trade.assignments.some(ass => ass.isChanged)) {
        const totalItems = getTotals(trade.assignments, 'total_scope_items');
        const completedItems = getTotals(trade.assignments, 'completed_scope_items');
        const reviewedItems = getTotals(trade.assignments, 'reviewed_scope_items');

        if (trade.status === TakeoffStatusEnum.IN_PROGRESS_QC && completedItems === totalItems) {
          setOpenConfirmationModal(trade.status);
        } else if (trade.status === TakeoffStatusEnum.IN_PROGRESS_QA && reviewedItems === totalItems) {
          setOpenConfirmationModal(trade.status);
        } else {
          handleSave();
        }
      }
    });
  };

  useEffect(() => {
    if (open) {
      const getScope = async (): Promise<void> => {
        try {
          setFetchingAssignments(true);
          const response = await getAPI<TakeoffAssignmentScopeType>(`${baseUrl}/takeoff/${requestId}/assignments/`);
          setFetchingAssignments(false);
          setInitialAssignments(response, authenticatedUser);
        } catch (err) {
          const error = (err as ErrorType)?.response?.data;
          showMessage(error?.detail, 'error');
          setFetchingAssignments(false);
        }
      };

      getScope();
    }
  }, [open, requestId, baseUrl, user]);

  return (
    <StyleProvider>
      <div>
        <Drawer
          show={open}
          anchor='right'
          onClose={() => {
            resetStore();
            setClose(false);
          }}
          width='19.8125rem'
          showCloseIcon
          renderDrawerContent={() => {
            return (
              <div>
                {fetchingAssignments ? (
                  <div className={loader}>
                    <Progress type='circular' size='5rem' />
                  </div>
                ) : (
                  <>
                    <div className={drawerHeader}>
                      <div className={drawerHeaderLine}>
                        <div className={styledFlex}>
                          Scope division
                          {userAssignment && (
                            <div className={completionTag}>
                              {isQA
                                ? `${totalReviewedItems}/${totalScopeItems} QA done`
                                : `${totalCompletedItems}/${totalScopeItems} QC done`}
                            </div>
                          )}
                        </div>
                        <CloseIcon
                          onClick={() => {
                            resetStore();
                            setClose(false);
                          }}
                        />
                      </div>
                      {userAssignment && (
                        <div className={infoText}>
                          {isQC && 'Add your checklist items and est.hours'}
                          {isQA && 'Mark checklist items as QA done'}
                        </div>
                      )}
                    </div>
                    <div className={styledDrawer}>
                      <StyledSelect
                        value={selectedTrade}
                        items={Object.entries(trades).map(([key, data]) => ({
                          label: data.tradeName,
                          value: key
                        }))}
                        onChange={(val: string) => setSelectedTrade(val)}
                        size='small'
                        dropdownWidth='8.6875rem'
                        variant='std-outlined'
                        className='trade-select'
                      />
                      <TradeScope
                        authenticatedUser={authenticatedUser}
                        assignments={trades[selectedTrade]?.assignments}
                        userPersona={userAssignment?.estimator_type}
                        tradeId={selectedTrade}
                      />
                      <FilterFooter>
                        <div className='box'>
                          <Button variant='contained' onClick={verifyStatusUpdate} loading={btnLoading}>
                            Save
                          </Button>
                        </div>
                      </FilterFooter>
                    </div>
                  </>
                )}
              </div>
            );
          }}
        />
        {openConfirmationModal ? (
          <StatusUpdateConfirmationModal
            show={!!openConfirmationModal}
            onClose={() => setOpenConfirmationModal(null)}
            status={openConfirmationModal as TakeoffStatusEnum.IN_PROGRESS_QA | TakeoffStatusEnum.IN_PROGRESS_QC}
            onYes={handleSave}
            yesBtnLoading={btnLoading}
          />
        ) : null}
      </div>
    </StyleProvider>
  );
};
